import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import moment from 'moment'
import { APIService } from 'api/APIService'
import { BackContext } from 'components/Utility/HashRouteModal/HashRouteModal'
import { StatusModal } from 'components/Utility/StatusModal/StatusModal'
import { ConfirmPhoneForm } from '../ConfirmPhone/ConfirmPhoneForm'
import { RecoveryForm } from './RecoveryForm'
import { NewPasswordForm } from './NewPasswordForm'

type Step = 'phone' | 'code' | 'new-password' | 'success'

type RecoveryFormControllerProps = {
  onSuccess: () => void,
}

/** Password recovery (#recover): phone, SMS code, new password */
export const RecoveryFormController: React.FC<RecoveryFormControllerProps> = ({ onSuccess }) => {
  const [step, setStep] = useState<Step>('phone')
  const backContext = useContext(BackContext)
  const recoveryData = useRef({ phone: '', code: '', password: '' })
  const [confirmCodeTime, setConfirmCodeTime] = useState<string | null>(null)
  const [isCodeSending, setCodeSending] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const sendCode = useCallback(async () => {
    setCodeSending(true)
    try {
      const time = await APIService.auth.confirmPhone(recoveryData.current.phone, 'change-password')
      setConfirmCodeTime(time.expires_at)
    } catch {
      // The code may still have been sent; the countdown then just starts from now
    } finally {
      setCodeSending(false)
    }
  }, [])

  const handlePhoneSubmit = useCallback(async ({ phone }: { phone: string }) => {
    recoveryData.current.phone = phone
    await sendCode()
    setStep('code')
  }, [sendCode])

  const handleCodeSubmit = useCallback(({ code }: { code: string }) => {
    recoveryData.current.code = code
    setStep('new-password')
  }, [])

  const handlePasswordSubmit = useCallback(async ({ password }: { password: string }) => {
    recoveryData.current.password = password
    const { phone, code } = recoveryData.current
    try {
      await APIService.auth.changePassword(phone, password, code)
      setStep('success')
    } catch (e) {
      setSubmitError((e as Error).message)
    }
  }, [])

  useEffect(() => {
    backContext.current = (step === 'code' || step === 'new-password') ? () => setStep('phone') : null
    return () => {
      backContext.current = null
    }
  }, [step, backContext])

  if (step === 'success') {
    return (
      <StatusModal
        isOpen
        modalText={'Пароль успешно изменён!'}
        status={'success'}
        buttonText={'ОК'}
        onClose={onSuccess}
        onButtonClick={onSuccess}
      />
    )
  }

  return (
    <>
      {step === 'phone' && <RecoveryForm onSubmit={handlePhoneSubmit} />}
      {step === 'code' && (
        <ConfirmPhoneForm
          onResend={sendCode}
          onSubmit={handleCodeSubmit}
          isSubmitting={isCodeSending}
          reclaimAt={moment(confirmCodeTime).format('YYYY-MM-DD HH:mm:ss')}
        />
      )}
      {step === 'new-password' && <NewPasswordForm onSubmit={handlePasswordSubmit} />}
      <StatusModal
        isOpen={submitError !== null}
        modalText={submitError}
        status={'error'}
        buttonText={'ОК'}
        onClose={() => setSubmitError(null)}
        onButtonClick={() => setSubmitError(null)}
      />
    </>
  )
}
